<template>
    <admin-template>
        <div class="space-y-6">

            <!-- Header -->
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-2xl font-black tracking-tight text-black">Paiements</h1>
                    <p class="text-sm text-gray-400 mt-0.5">Suivi des commissions à verser</p>
                </div>
                <div class="px-4 py-2 rounded-xl bg-orange-50 border border-orange-100 text-right">
                    <div class="text-xs font-bold text-orange-400 uppercase tracking-wider">Total dû (sélection)</div>
                    <div class="text-xl font-black text-orange-500">{{ totalDuSelection | eur }}</div>
                </div>
            </div>

            <!-- Filtres -->
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-end gap-4">
                <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Commercial</label>
                    <select v-model="filterComId" @change="loadPaiements" class="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white transition-all">
                        <option value="">Tous les commerciaux</option>
                        <option v-for="c in commerciaux" :key="c.id" :value="c.id">{{ c.nom }}</option>
                    </select>
                </div>
                <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Année</label>
                    <select v-model="filterAnnee" @change="onAnneeChange" class="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white transition-all">
                        <option v-for="y in anneesDisponibles" :key="y" :value="y">{{ y }}</option>
                    </select>
                </div>
                <div>
                    <label class="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Mois</label>
                    <select v-model="filterMois" @change="loadPaiements" class="px-4 py-2.5 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white transition-all">
                        <option value="">Tous les mois</option>
                        <option v-for="m in moisDisponibles" :key="m.v" :value="m.v">{{ m.l }}</option>
                    </select>
                </div>
                <div class="ml-auto flex items-center gap-2">
                    <span class="text-xs text-gray-400">{{ filterMois ? (moisDisponibles.find(m => m.v == filterMois) || {}).l : 'Toute l\'année' }} {{ filterAnnee }}</span>
                </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center text-gray-300">
                <i class="fas fa-spinner fa-spin text-3xl mb-3 block"></i>
                <p class="text-sm">Chargement des paiements...</p>
            </div>

            <!-- Aucun résultat -->
            <div v-else-if="paiementsGrouped.length === 0" class="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center text-gray-300">
                <i class="fas fa-inbox text-3xl mb-3 block"></i>
                <p class="text-sm font-bold">Aucune commission pour cette sélection</p>
            </div>

            <!-- Liste groupée par commercial -->
            <div v-else class="space-y-4">
                <div v-for="group in paiementsGrouped" :key="group.commercial_id" class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

                    <!-- En-tête commercial -->
                    <div class="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div class="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-black" :style="{ background: avatarColor(group.nom) }">
                                {{ initials(group.nom) }}
                            </div>
                            <div>
                                <div class="font-black text-black text-sm">{{ group.nom }}</div>
                                <div class="text-xs text-gray-400">{{ group.lignes.length }} période{{ group.lignes.length > 1 ? 's' : '' }}</div>
                            </div>
                        </div>
                        <div class="text-right">
                            <div class="text-xs text-gray-400 font-bold uppercase tracking-wider">Dû sur la sélection</div>
                            <div class="text-lg font-black" :class="group.totalDuLignes > 0 ? 'text-orange-500' : 'text-green-500'">
                                {{ group.totalDuLignes | eur }}
                            </div>
                        </div>
                    </div>

                    <!-- Lignes du mois -->
                    <div class="divide-y divide-gray-50">
                        <div v-for="p in group.lignes" :key="p.periode"
                            class="px-6 py-4 flex items-center gap-4 transition-colors"
                            :class="p.tout_paye ? 'opacity-60' : 'hover:bg-gray-50'">

                            <!-- Période -->
                            <div class="w-32 flex-shrink-0">
                                <div class="font-bold text-sm text-black">{{ p.mois | moisLabel }} {{ p.annee }}</div>
                                <div class="text-xs text-gray-400">{{ p.nb_clients }} client{{ p.nb_clients > 1 ? 's' : '' }}</div>
                            </div>

                            <!-- Montant total du mois -->
                            <div class="flex-1">
                                <div class="text-sm font-black text-black">{{ p.total_mois | eur }}</div>
                                <div class="text-xs text-gray-400">total commissions du mois</div>
                            </div>

                            <!-- Dû -->
                            <div class="w-28 text-right flex-shrink-0">
                                <span class="px-3 py-1.5 rounded-full text-xs font-bold"
                                    :class="p.tout_paye ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'">
                                    {{ p.tout_paye ? '✓ Versé' : (p.total_du | eur) + ' dû' }}
                                </span>
                            </div>

                            <!-- Action -->
                            <div class="w-36 text-right flex-shrink-0">
                                <span v-if="p.tout_paye" class="text-xs text-green-500 font-bold flex items-center justify-end gap-1">
                                    <i class="fas fa-check-circle"></i> Payé
                                </span>
                                <button v-else
                                    @click="payerMois(group.commercial_id, p)"
                                    :disabled="payingKey === group.commercial_id + '-' + p.periode"
                                    class="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold hover:shadow-md transition-all disabled:opacity-50 flex items-center gap-1.5 ml-auto">
                                    <i class="fas fa-paper-plane"></i>
                                    {{ payingKey === group.commercial_id + '-' + p.periode ? '...' : 'Marquer versé' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- Toast -->
        <transition name="toast">
            <div v-if="toast.show" class="fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl text-sm font-bold"
                :class="toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'">
                <i :class="toast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                {{ toast.message }}
            </div>
        </transition>

    </admin-template>
</template>

<script>

export default {
    name: 'AdminCommerciauxPaiements',

    filters: {
        eur(v) {
            if (v === null || v === undefined) return '—';
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
        },
        moisLabel(m) {
            return ['','Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'][m] || m;
        },
    },

    data() {
        return {
            loading: false,
            commerciaux: [],
            paiementsData: [],
            filterComId: '',
            filterAnnee: String(new Date().getFullYear()),
            filterMois: '',
            payingKey: null,
            toast: { show: false, message: '', type: 'success' },
        };
    },

    computed: {
        anneesDisponibles() {
            const cur = new Date().getFullYear();
            const years = [];
            for (let y = 2024; y <= cur; y++) years.push(String(y));
            return years;
        },

        moisDisponibles() {
            const all = [
                {v:'1',l:'Janvier'},{v:'2',l:'Février'},{v:'3',l:'Mars'},{v:'4',l:'Avril'},
                {v:'5',l:'Mai'},{v:'6',l:'Juin'},{v:'7',l:'Juillet'},{v:'8',l:'Août'},
                {v:'9',l:'Septembre'},{v:'10',l:'Octobre'},{v:'11',l:'Novembre'},{v:'12',l:'Décembre'},
            ];
            // Si année courante : bloquer les mois futurs
            if (this.filterAnnee === String(new Date().getFullYear())) {
                return all.filter(m => parseInt(m.v) <= new Date().getMonth() + 1);
            }
            return all;
        },

        paiementsGrouped() {
            return this.paiementsData
                .filter(g => !this.filterComId || g.commercial_id == this.filterComId)
                .map(g => {
                    const lignes = g.recap.filter(r => !this.filterMois || String(r.mois) === String(this.filterMois));
                    return {
                        ...g,
                        lignes,
                        totalDuLignes: lignes.reduce((s, l) => s + (l.total_du || 0), 0),
                    };
                })
                .filter(g => g.lignes.length > 0);
        },

        totalDuSelection() {
            return this.paiementsGrouped.reduce((s, g) => s + (g.totalDuLignes || 0), 0);
        },
    },

    mounted() {
        this.fetchCommerciaux();
    },

    methods: {
        async fetchCommerciaux() {
            const r = await this.$request.get('/admin/commerciaux');
            this.commerciaux = r || [];
            await this.loadPaiements();
        },

        async loadPaiements() {
            this.loading = true;
            this.paiementsData = [];
            const targets = this.filterComId
                ? this.commerciaux.filter(c => c.id == this.filterComId)
                : this.commerciaux;

            for (const c of targets) {
                const r = await this.$request.get(`/admin/commerciaux/${c.id}/recap-paiements?annee=${this.filterAnnee}`);
                if (r) {
                    this.paiementsData.push({
                        commercial_id: c.id,
                        nom: c.nom,
                        total_du: r.total_global_du,
                        recap: r.recap || [],
                    });
                }
            }
            this.loading = false;
        },

        onAnneeChange() {
            // Réinitialiser le mois si invalide pour l'année courante
            const curMonth = new Date().getMonth() + 1;
            if (this.filterAnnee === String(new Date().getFullYear()) && this.filterMois && parseInt(this.filterMois) > curMonth) {
                this.filterMois = '';
            }
            this.loadPaiements();
        },

        async payerMois(commercialId, p) {
            this.payingKey = commercialId + '-' + p.periode;
            try {
                await this.$request.post(`/admin/commerciaux/${commercialId}/payer`, { mois: p.mois, annee: p.annee });
                this.showToast('Commission marquée comme versée ✓', 'success');
                await this.loadPaiements();
            } catch {
                this.showToast('Erreur lors du marquage', 'error');
            }
            this.payingKey = null;
        },

        initials(n) { return (n || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); },
        avatarColor(n) {
            const colors = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#F59E0B'];
            let h = 0; for (let i = 0; i < (n || '').length; i++) h = n.charCodeAt(i) + ((h << 5) - h);
            return colors[Math.abs(h) % colors.length];
        },
        showToast(message, type = 'success') {
            this.toast = { show: true, message, type };
            setTimeout(() => { this.toast.show = false; }, 4000);
        },
    },
};
</script>

<style scoped>
.toast-enter-active, .toast-leave-active { transition: all 0.3s ease; }
.toast-enter, .toast-leave-to { opacity: 0; transform: translateY(12px); }
</style>